/**
 * DETAIL PAGE LAYOUT
 * Layout commun pour les pages de détail (Contact, Crew, etc.)
 *
 * Structure:
 * - Header avec bouton retour et breadcrumb
 * - Section header avec titre, sous-titre (badges/infos) et actions
 * - Navbar avec onglets
 * - Contenu de l'onglet actif
 */

import { type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';

interface Breadcrumb {
  label: string;
  href?: string;
}

interface Tab {
  id: string;
  label: string;
}

interface DetailPageLayoutProps {
  /** Fil d'Ariane (breadcrumb) */
  breadcrumbs: Breadcrumb[];
  /** Titre principal de la page */
  title: string;
  /** Éléments affichés sous le titre (badges, infos) */
  subtitle?: ReactNode;
  /** Boutons d'action à droite du header */
  actions?: ReactNode;
  /** Configuration des onglets */
  tabs: Tab[];
  /** ID de l'onglet actif */
  activeTab: string;
  /** Callback de changement d'onglet */
  onTabChange: (tabId: string) => void;
  /** Contenu de la page */
  children: ReactNode;
}

export function DetailPageLayout({
  breadcrumbs,
  title,
  subtitle,
  actions,
  tabs,
  activeTab,
  onTabChange,
  children,
}: DetailPageLayoutProps) {
  return (
    <div className="flex h-screen flex-col">
      {/* Header avec breadcrumb */}
      <div className="bg-white flex h-16 items-center px-6 shadow-sm">
        <div className="flex items-center gap-2">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 text-sm">
            {breadcrumbs.map((crumb, index) => (
              <div key={index} className="flex items-center gap-2">
                {index > 0 && <ChevronRight className="h-4 w-4 text-zinc-400" />}
                {crumb.href ? (
                  <Link to={crumb.href} className="text-zinc-500 hover:text-zinc-900">
                    {crumb.label}
                  </Link>
                ) : (
                  <span className="text-zinc-900 font-medium">{crumb.label}</span>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Section Header avec titre et actions */}
      <div className="bg-white px-6 py-6 border-b border-t border-zinc-200">
        <div className="flex items-end justify-between">
          <div className="flex flex-col gap-1">
            <h1 className="text-xl font-semibold text-zinc-900">{title}</h1>
            {subtitle && (
              <div className="flex items-center gap-2 text-xs text-zinc-900">{subtitle}</div>
            )}
          </div>

          {/* Boutons d'action */}
          {actions && <div className="flex items-center gap-1.5">{actions}</div>}
        </div>
      </div>

      {/* Navbar avec onglets */}
      <div className="bg-white px-6 h-16 flex items-center border-b border-zinc-200">
        <div className="flex gap-1">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => onTabChange(tab.id)}
              className={`px-3 py-2.5 text-sm font-medium rounded-md transition-colors ${
                activeTab === tab.id ? 'text-zinc-900' : 'text-zinc-500 hover:text-zinc-900'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Contenu */}
      {children}
    </div>
  );
}
